import React from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import NavigationPrompt from 'react-router-navigation-prompt';
import { isEmpty } from 'lodash';
import compose from 'compose-function';
import { ApButtonTab, ApSecondaryNav } from '@alixpartners/ui-components';

import withMutation from 'components/withMutation';
import { PageToolbarBlockButton } from 'components/PageToolbar';
import Modal from 'components/Modal';
import { SettingsContext } from 'contexts/SettingsContext';
import { SpendCubeSettingsContext } from 'contexts/SpendCubeSettingsContext';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { ClassificationContext } from 'contexts/ClassificationContext';

import { Bin } from './Bin';

import { OpportunityYearSection } from './OpportunityYearSection';
import { CustomFiltersSection } from './CustomFiltersSection';
import { BrandSection } from './BrandSection';
import ClassificationSection from './ClassificationSection';
import VendorsSection from './VendorsSection';
import CompanyMetaData from './CompanyMetadata';
import Benchmark from './Benchmark/Benchmark';
import { ExportMetadataSectionContainer } from './ExportMetadataSection';
import FirstUsageSettings from './FirstUsageSettings';
import { UploadStateContext } from '../contexts/UploadStateContext';
import {
	PermissionsContext,
	PERMISSIONS,
} from '../contexts/PermissionsContext';
import { ApiDataContext } from 'contexts/ApiDataContext';
import {
	SettingsContent,
	Form,
	CenterSectionWrapper,
	BuildVersion,
} from './styles';
import {
	validateThresholds,
	serializePeriod,
	getTailSpendAnalysisConfigurationForSave,
	CUSTOM_CURRENCY_TYPE,
} from './utils';
import {
	saveSettings,
	saveClassificationSettings,
	deleteTailSpendAnalysis,
	getPorfolioCompanies,
} from '../utils/apiRequests';
import { UPLOAD_STATUS } from 'Data/constants';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import AccessControl from './AccessControl/AccessControl';
import AliasList from 'Settings/Alias List';

const { REACT_APP_BUILD_VERSION } = process.env;

export const THRESHOLD_PROP_TYPE = PropTypes.oneOfType([
	PropTypes.string,
	PropTypes.number,
]).isRequired;

const DEFAULT_PATH = '/settings';
const EXPORT_PAGE_PATH = '/settings/archive';
const SPEND_CUBE_PAGE_PATH = '/settings/spend-cube';
const ACCESS_CONTROL_PATH = '/settings/access-control';
const BRAND_PAGE_PATH = '/settings/brand';
const VENDORS_PAGE_PATH = '/settings/vendors';
const CLASSIFICATION_PAGE_PATH = '/settings/classification';
const BENCHMARKS_PAGE_PATH = '/settings/benchmarks';
const COMPANY_METADATA_PATH = '/settings/company-metadata';
const BIN_PATH = '/settings/recycle';
const ALIAS_ACCESS_PATH = '/settings/alias-access';

export const UNITS = [
	{ value: 'Thousand (K)', key: 'k', worth: 1000 },
	{ value: 'Million (M)', key: 'm', worth: 1000000 },
	{ value: 'Billion (B)', key: 'b', worth: 1000000000 },
];

class SettingsBase extends React.Component {
	static propTypes = {
		opportunityYear: PropTypes.string,
		hideDecimals: PropTypes.bool.isRequired,
		aliasName: PropTypes.string,
		appTitle: PropTypes.string.isRequired,
		backgroundColor: PropTypes.string.isRequired,
		foregroundColor: PropTypes.string.isRequired,
		threshold1: THRESHOLD_PROP_TYPE,
		threshold2: THRESHOLD_PROP_TYPE,
		updateSettingsConfig: PropTypes.func.isRequired,
	};

	state = {
		opportunityYear: this.props.opportunityYear,
		customFilters: this.props.customFilters,
		hideDecimals: this.props.hideDecimals,
		aliasName: this.props.aliasName,
		companyName: this.props.companyName,
		appTitle: this.props.appTitle,
		backgroundColor: this.props.backgroundColor,
		foregroundColor: this.props.foregroundColor,
		threshold1: this.props.threshold1,
		threshold2: this.props.threshold2,
		isStale: false,
		tailSpendHasChanged: false,
		isEmptyMetaData: null,
		anonymizationLevel: null,
		addAnonymizationLevelStep: false,
		serverMetaData: null,
		firstUsageLoading: true,
		spendCubeSettings: {
			currency: null,
			period: '',
			initiativePeriod: '',
			tailSpend: null,
			tailSpendType: 0,
			categoryValue: 1,
			categoryUnit: 'm',
			vendorSpendValue: 1,
			vendorSpendUnit: 'm',
			customPeriods: [],
			customInititivesPeriods: [],
			shouldRefresh: false,
			customCurrency: {},
			legalEntity: [],
			companyId: null,
			isPortco: null,
		},
		classificationSettings: {
			columns: [],
		},
		portfolioCompanies: {
			options: [],
			isLoading: false,
		},
	};

	generatedCustomNames = [];

	componentWillUnmount() {
		this.clearStaleState();
	}
	componentDidMount() {
		this.setState({
			portfolioCompanies: {
				...this.portfolioCompanies,
				isLoading: true,
			},
		});
		getPorfolioCompanies().then(porfolioResponse => {
			let companiesOptions;
			if (porfolioResponse.data !== null && porfolioResponse.data.length > 0) {
				companiesOptions = porfolioResponse.data.map(x => {
					return {
						value: x,
						label: x,
					};
				});
			}
			this.setState({
				portfolioCompanies: {
					options: companiesOptions,
					isLoading: false,
				},
			});
		});
	}

	firstUsageIsLoading = value => {
		this.setState({
			firstUsageLoading: value,
		});
	};
	render() {
		const {
			companiesMetadata,
			match,
			location,
			history,
			status,
			permissions,
			taxonomyVersion,
			isParent,
		} = this.props;
		const {
			opportunityYear,
			customFilters,
			hideDecimals,
			aliasName,
			companyName,
			appTitle,
			backgroundColor,
			foregroundColor,
			threshold1,
			threshold2,
			isStale,
			isFormValid,
			isEmptyMetaData,
			addAnonymizationLevelStep,
			spendCubeSettings,
			firstUsageLoading,
			classificationSettings,
			portfolioCompanies,
		} = this.state;
		const errors = validateThresholds({ threshold1, threshold2 });
		const isOnDefaultPage = match.path === DEFAULT_PATH;
		const isOnSpendCubePage = match.path === SPEND_CUBE_PAGE_PATH;
		const isOnAccessControlPage = match.path === ACCESS_CONTROL_PATH;
		const isOnArchivePage = match.path === EXPORT_PAGE_PATH;
		const isOnClassificationPage = match.path === CLASSIFICATION_PAGE_PATH;
		const isOnBrandPage = match.path === BRAND_PAGE_PATH;
		const isOnVendorsPage = match.path === VENDORS_PAGE_PATH;
		const isOnCompanyMetadataPage = match.path === COMPANY_METADATA_PATH;
		const isOnBinPage = match.path === BIN_PATH;
		const isOnBenchmarksPage = match.path === BENCHMARKS_PAGE_PATH;
		const isOnAliasAccessPage = match.path === ALIAS_ACCESS_PATH;
		const isOnEditExportPage =
			isOnArchivePage &&
			(location.pathname === `${EXPORT_PAGE_PATH}/edit/metadata` ||
				location.pathname === `${EXPORT_PAGE_PATH}/edit/anonymization`);
		const isOnSavedExportPage =
			isOnArchivePage && location.pathname === `${EXPORT_PAGE_PATH}/saved`;
		const hideSaveBtn =
			isOnCompanyMetadataPage ||
			(isOnArchivePage && !isOnEditExportPage) ||
			isOnBenchmarksPage;
		const editPage = isOnEditExportPage
			? location.pathname.substr(location.pathname.lastIndexOf('/') + 1)
			: '';
		let saveButtonTitle = 'Apply Settings';
		if (isOnEditExportPage && isEmptyMetaData) {
			saveButtonTitle = 'Next';
		} else if (isOnEditExportPage) {
			saveButtonTitle =
				editPage === 'metadata' ? 'Save Metadata' : 'Set Anonymization level';
		} else if (isOnAccessControlPage) {
			saveButtonTitle = 'Save Access Control';
		}

		let saveButtonOnClick;

		if (isOnArchivePage) {
			saveButtonOnClick = this.handleMetadataSave;
		} else if (isOnBrandPage) {
			saveButtonOnClick = this.handleSaveBrand;
		} else if (isOnClassificationPage) {
			saveButtonOnClick = this.handleSaveClassification;
		} else if (isOnAccessControlPage) {
			saveButtonOnClick = this.handleSaveSpendCube;
		} else {
			saveButtonOnClick = this.handleSaveSpendCube;
		}

		let saveButtonDisabled;
		if (isOnArchivePage || isOnSpendCubePage) {
			saveButtonDisabled = !isStale || !isFormValid;
		} else {
			saveButtonDisabled = !isStale || !isEmpty(errors);
		}

		const isFirstTimeUsage = status.state === UPLOAD_STATUS.CLASSIFIED;
		const isRequiredToFillCompanies =
			isFirstTimeUsage && !companiesMetadata.allCompaniesConfigured;

		let contentBlock;
		if (isOnAccessControlPage) {
			contentBlock = (
				<CenterSectionWrapper>
					<AccessControl
						onChange={this.handleSpendCubeChange}
						companyId={spendCubeSettings.companyId}
					/>
				</CenterSectionWrapper>
			);
		} else if (isOnArchivePage) {
			contentBlock = (
				<ExportMetadataSectionContainer
					editPage={editPage}
					isSaved={isOnSavedExportPage}
					onChange={this.handleChange}
					history={history}
					onLoad={this.onMetadataLoad}
					addAnonymizationLevelStep={addAnonymizationLevelStep}
					clearStaleState={this.clearStaleState}
					location={location}
				/>
			);
		} else if (isOnBrandPage) {
			contentBlock = (
				<CenterSectionWrapper>
					<BrandSection
						animationIndex={4}
						aliasName={aliasName}
						companyName={companyName}
						appTitle={appTitle}
						backgroundColor={backgroundColor}
						foregroundColor={foregroundColor}
						onChange={this.handleChange}
					/>
				</CenterSectionWrapper>
			);
		} else if (isOnVendorsPage) {
			contentBlock = (
				<CenterSectionWrapper>
					<VendorsSection
						animationIndex={4}
						companyName={companyName}
						appTitle={appTitle}
						backgroundColor={backgroundColor}
						foregroundColor={foregroundColor}
						onChange={this.handleChange}
						permissions={permissions}
					/>
				</CenterSectionWrapper>
			);
		} else if (isOnClassificationPage) {
			contentBlock = (
				<CenterSectionWrapper>
					<ClassificationSection
						animationIndex={1}
						data={classificationSettings}
						onChange={this.handleClassificationChange}
					/>
				</CenterSectionWrapper>
			);
		} else if (isOnCompanyMetadataPage) {
			contentBlock = (
				<CenterSectionWrapper>
					<CompanyMetaData />
				</CenterSectionWrapper>
			);
		} else if (isOnBinPage) {
			contentBlock = (
				<CenterSectionWrapper>
					<Bin />
				</CenterSectionWrapper>
			);
		} else if (isOnBenchmarksPage) {
			contentBlock = (
				<CenterSectionWrapper>
					<Benchmark />
				</CenterSectionWrapper>
			);
		} else if (isOnAliasAccessPage) {
			contentBlock = (
				<CenterSectionWrapper>
					<AliasList />
				</CenterSectionWrapper>
			);
		} else {
			// while loading, just want one set of panels
			if (isRequiredToFillCompanies) {
				contentBlock = (
					<CenterSectionWrapper>
						<CompanyMetaData />
					</CenterSectionWrapper>
				);
			} else if (firstUsageLoading) {
				contentBlock = (
					<Form>
						<FirstUsageSettings
							value={spendCubeSettings}
							permissions={permissions}
							isParent={isParent}
							status={status}
							portfolioCompanies={portfolioCompanies}
							onStateChange={this.handleChange}
							onChange={this.handleSpendCubeChange}
							setLoading={this.firstUsageIsLoading}
							hideDecimals={hideDecimals}
							location={location}
							onApplySettings={saveButtonOnClick}
							saveButtonDisabled={saveButtonDisabled}
						/>
					</Form>
				);
			} else {
				contentBlock = (
					<Form>
						<FirstUsageSettings
							value={spendCubeSettings}
							permissions={permissions}
							isParent={isParent}
							status={status}
							portfolioCompanies={portfolioCompanies}
							onStateChange={this.handleChange}
							onChange={this.handleSpendCubeChange}
							setLoading={this.firstUsageIsLoading}
							hideDecimals={hideDecimals}
							location={location}
							onApplySettings={saveButtonOnClick}
							saveButtonDisabled={saveButtonDisabled}
						/>
						{(permissions.includes(PERMISSIONS.PlansRead) ||
							permissions.includes(PERMISSIONS.ExecuteRead)) && (
							<OpportunityYearSection
								animationIndex={0}
								opportunityYear={opportunityYear}
								onChange={this.handleChange}
							/>
						)}

						<CustomFiltersSection
							animationIndex={1}
							customFilters={customFilters}
							onChange={this.handleChange}
						/>
					</Form>
				);
			}
		}

		return (
			<React.Fragment>
				<ApSecondaryNav
					left={
						<React.Fragment>
							{!isRequiredToFillCompanies &&
								(isFirstTimeUsage || status.isDataPublished) &&
								permissions.includes(PERMISSIONS.SettingsWrite) && (
									<NavLink exact to={SPEND_CUBE_PAGE_PATH}>
										<ApButtonTab
											isSelected={isOnDefaultPage || isOnSpendCubePage}
											onClick={e => {
												this.setState({ firstUsageLoading: true });
												if (isFirstTimeUsage) {
													e.preventDefault();
												}
											}}
										>
											Spend Cube
										</ApButtonTab>
									</NavLink>
								)}
							{permissions.includes(PERMISSIONS.AccessControlRead) &&
								spendCubeSettings.isPortco && (
									<NavLink exact to={ACCESS_CONTROL_PATH}>
										<ApButtonTab
											isSelected={isOnAccessControlPage}
											onClick={e => {
												if (isFirstTimeUsage) {
													e.preventDefault();
												}
											}}
										>
											Access Control
										</ApButtonTab>
									</NavLink>
								)}

							{!isRequiredToFillCompanies &&
								status.isDataPublished &&
								permissions.includes(PERMISSIONS.SettingsArchiveWrite) && (
									<NavLink disabled={isFirstTimeUsage} to={EXPORT_PAGE_PATH}>
										<ApButtonTab
											disabled={isFirstTimeUsage}
											isSelected={isOnArchivePage}
											onClick={e => {
												this.setState({ firstUsageLoading: true });
												if (isFirstTimeUsage) {
													e.preventDefault();
												}
											}}
										>
											Archive
										</ApButtonTab>
									</NavLink>
								)}
							{!isRequiredToFillCompanies &&
								status.isDataPublished &&
								permissions.includes(PERMISSIONS.SettingsWrite) && (
									<NavLink disabled={isFirstTimeUsage} to={BRAND_PAGE_PATH}>
										<ApButtonTab
											disabled={isFirstTimeUsage}
											isSelected={isOnBrandPage}
											onClick={e => {
												if (isFirstTimeUsage) {
													e.preventDefault();
												}
											}}
										>
											Brand
										</ApButtonTab>
									</NavLink>
								)}
							{!isRequiredToFillCompanies &&
								status.isDataPublished &&
								permissions.includes(PERMISSIONS.CCORead) && (
									<NavLink disabled={isFirstTimeUsage} to={VENDORS_PAGE_PATH}>
										<ApButtonTab
											disabled={isFirstTimeUsage}
											isSelected={isOnVendorsPage}
											onClick={e => {
												if (isFirstTimeUsage) {
													e.preventDefault();
												}
											}}
										>
											Vendors
										</ApButtonTab>
									</NavLink>
								)}
							{permissions.includes(
								PERMISSIONS.SettingsClassificationWrite
							) && (
								<NavLink
									disabled={isFirstTimeUsage}
									onClick={e => {
										if (isFirstTimeUsage) {
											e.preventDefault();
										}
									}}
									to={CLASSIFICATION_PAGE_PATH}
								>
									<ApButtonTab
										disabled={isFirstTimeUsage}
										isSelected={isOnClassificationPage}
										onClick={e => {
											if (isFirstTimeUsage) {
												e.preventDefault();
											}
										}}
									>
										Classification
									</ApButtonTab>
								</NavLink>
							)}
							{status.isDataPublished && (
								<NavLink
									disabled={isFirstTimeUsage && !isRequiredToFillCompanies}
									to={COMPANY_METADATA_PATH}
								>
									<ApButtonTab
										disabled={isFirstTimeUsage && !isRequiredToFillCompanies}
										isSelected={
											isRequiredToFillCompanies || isOnCompanyMetadataPage
										}
										onClick={e => {
											if (isFirstTimeUsage) {
												e.preventDefault();
											}
										}}
									>
										Company metadata
									</ApButtonTab>
								</NavLink>
							)}
							{!isRequiredToFillCompanies &&
								status.isDataPublished &&
								permissions.includes(PERMISSIONS.BenchMarkWrite) &&
								permissions.includes(PERMISSIONS.SettingsWrite) && (
									<NavLink
										disabled={isFirstTimeUsage}
										to={BENCHMARKS_PAGE_PATH}
									>
										<ApButtonTab
											disabled={isFirstTimeUsage}
											isSelected={isOnBenchmarksPage}
											onClick={e => {
												if (isFirstTimeUsage) {
													e.preventDefault();
												}
											}}
										>
											Benchmarks
										</ApButtonTab>
									</NavLink>
								)}
							{status.isDataPublished && (
								<NavLink disabled={isFirstTimeUsage} to={BIN_PATH}>
									<ApButtonTab
										disabled={isFirstTimeUsage}
										isSelected={isOnBinPage}
										onClick={e => {
											if (isFirstTimeUsage) {
												e.preventDefault();
											}
										}}
									>
										Bin
									</ApButtonTab>
								</NavLink>
							)}
							{status.isDataPublished &&
								permissions.includes(PERMISSIONS.AliasListRead) && (
									<NavLink disabled={isFirstTimeUsage} to={ALIAS_ACCESS_PATH}>
										<ApButtonTab
											disabled={isFirstTimeUsage}
											isSelected={isOnAliasAccessPage}
											onClick={e => {
												if (isFirstTimeUsage) {
													e.preventDefault();
												}
											}}
										>
											Alias Access
										</ApButtonTab>
									</NavLink>
								)}
						</React.Fragment>
					}
					right={
						hideSaveBtn ? null : (
							<React.Fragment>
								<PageToolbarBlockButton
									disabled={saveButtonDisabled}
									onClick={saveButtonOnClick}
								>
									{saveButtonTitle}
								</PageToolbarBlockButton>
							</React.Fragment>
						)
					}
				/>
				<SettingsContent>
					{contentBlock}

					{(REACT_APP_BUILD_VERSION || taxonomyVersion) && (
						<BuildVersion>
							{REACT_APP_BUILD_VERSION && (
								<React.Fragment>
									{`Product version: ${REACT_APP_BUILD_VERSION}`}
									<br />
								</React.Fragment>
							)}
							{taxonomyVersion && `Taxonomy Version: ${taxonomyVersion}`}
						</BuildVersion>
					)}
				</SettingsContent>

				<NavigationPrompt
					disableNative
					when={(currentLocation, nextLocation) =>
						currentLocation.pathname !== nextLocation.pathname && isStale
					}
				>
					{({ onConfirm, onCancel }) => (
						<Modal
							cancelLabel="Cancel"
							submitLabel="Continue"
							onSubmit={onConfirm}
							onClose={onCancel}
						>
							Navigating away will lose the changes you made on this screen.
						</Modal>
					)}
				</NavigationPrompt>
			</React.Fragment>
		);
	}

	handleChange = (state, skipStale) => {
		this.setState({ ...state, isStale: !skipStale });
	};

	handleClassificationChange = (state, skipStale) => {
		this.setState({
			classificationSettings: {
				...this.state.classificationSettings,
				...state,
			},
			isStale: !skipStale,
		});
	};

	handleSpendCubeChange = ({ tailSpendHasChanged, ...state }, afterChange) => {
		this.setState(
			{
				spendCubeSettings: { ...this.state.spendCubeSettings, ...state },
				isStale: true,
				tailSpendHasChanged:
					this.state.tailSpendHasChanged || tailSpendHasChanged,
			},
			() => {
				if (typeof afterChange === 'function') {
					afterChange();
				}
			}
		);
	};

	handleSaveClassification = () => {
		const { classificationSettings } = this.state;
		const { refetchClassification } = this.props;

		saveClassificationSettings(classificationSettings).then(() => {
			refetchClassification();
			this.setState({
				isStale: false,
			});
		});
	};

	handleSaveExtra = () => {
		const { updateSettingsConfig } = this.props;

		const {
			opportunityYear,
			hideDecimals,
			threshold1,
			threshold2,
		} = this.state;

		updateSettingsConfig({
			opportunityYear,
			hideDecimals,
			threshold1,
			threshold2,
		}).then(() => {
			this.setState({
				isStale: false,
			});
		});
	};

	handleSaveBrand = () => {
		const { updateSettingsConfig } = this.props;

		const {
			aliasName,
			companyName,
			appTitle,
			backgroundColor,
			foregroundColor,
		} = this.state;

		updateSettingsConfig({
			aliasName,
			companyName,
			appTitle,
			backgroundColor,
			foregroundColor,
		}).then(() => {
			this.setState({
				isStale: false,
			});
		});
	};

	generateUniqueName = (years, periods) => {
		let newName = `custom ${years}`;
		let i = 1;
		const findByName = item => item.name === newName;

		while (
			this.generatedCustomNames.includes(newName) ||
			periods.find(findByName)
		) {
			newName = `custom${i} ${years}`;

			i++;
		}

		this.generatedCustomNames = [...this.generatedCustomNames, newName];

		return newName;
	};

	getPeriodsData = () => {
		const {
			customPeriods,
			customInititivesPeriods,
			customInititivesTotalPeriods,
			period,
			initiativePeriod,
			initiativeTotalPeriod,
		} = this.state.spendCubeSettings;
		const hasId = !customPeriods.find(({ uid }) => uid === period);
		const hasInitiativeId = !customInititivesPeriods.find(
			({ uid }) => uid === initiativePeriod
		);
		const hasInitiativeTotalId = !customInititivesTotalPeriods.find(
			({ uid }) => uid === initiativeTotalPeriod
		);

		return {
			spendCubeDateRangeId: hasId ? Number(period) : null,
			initiativesDateRangeId: hasInitiativeId ? Number(initiativePeriod) : null,
			initiativesTotalSpendDateRangeId: hasInitiativeTotalId
				? Number(initiativeTotalPeriod)
				: null,
			customPeriods: customPeriods.map(p =>
				this.mapPeriodsToAPI(p, period, customPeriods)
			),
			customPeriodsForInitiatives: customInititivesPeriods.map(p =>
				this.mapPeriodsToAPI(p, initiativePeriod, customInititivesPeriods)
			),
			customPeriodsForInitiativesTotalSpend: customInititivesTotalPeriods.map(
				p =>
					this.mapPeriodsToAPI(
						p,
						initiativeTotalPeriod,
						customInititivesTotalPeriods
					)
			),
		};
	};

	mapPeriodsToAPI = ({ monthPickerRange, name, uid, id }, period, periods) => {
		const years =
			monthPickerRange.from.year === monthPickerRange.to.year
				? monthPickerRange.from.year.toString().slice(-2)
				: `${monthPickerRange.from.year
						.toString()
						.slice(-2)}-${monthPickerRange.to.year.toString().slice(-2)}`;

		return {
			...serializePeriod(monthPickerRange),
			name: name || this.generateUniqueName(years, periods),
			...(uid === period && { isSelected: true }),
			...(id && { id }),
		};
	};

	deleteTailSpendAnalysis = async () => {
		const { tailSpendAnalysisData } = this.props;

		if (
			tailSpendAnalysisData &&
			tailSpendAnalysisData.periodType &&
			this.state.tailSpendHasChanged
		) {
			await deleteTailSpendAnalysis();
		}

		this.setState({ tailSpendHasChanged: false });
	};

	handleSaveSpendCube = () => {
		const {
			categoryValue,
			categoryUnit,
			vendorSpendValue,
			vendorSpendUnit,
			tailSpend,
			tailSpendType,
			currency,
			tailSpendAnalysisConfiguration,
			customCurrency,
			useNetSpend,
			financialYearStartMonth,
			legalEntity,
			companyId,
			isPortco,
		} = this.state.spendCubeSettings;
		const {
			fetchState,
			refetchSettings,
			refetchSettingsOptions,
			status,
			clearApiCache,
			refetchSpendCubeSettings,
		} = this.props;
		const isFirstTimeUsage = status.state === UPLOAD_STATUS.CLASSIFIED;

		this.setState({ isStale: false });
		clearApiCache();
		this.setState({
			portfolioCompanies: {
				...this.portfolioCompanies,
				isLoading: true,
			},
		});
		return Promise.all([
			this.handleSaveExtra(),
			this.saveCustomFilters(),
			this.deleteTailSpendAnalysis(),
			saveSettings({
				legalEntity,
				companyId,
				isPortco,
				tailSpend,
				tailSpendType,
				useNetSpend,
				financialYearStartMonth,
				spendCubeCurrency:
					currency === CUSTOM_CURRENCY_TYPE ? customCurrency.code : currency,
				othersThreshold: this.getValueByUnitPair(categoryValue, categoryUnit),
				tailSpendAnalysisConfiguration: getTailSpendAnalysisConfigurationForSave(
					tailSpendAnalysisConfiguration
				),
				vendorSpendAmount: this.getValueByUnitPair(
					vendorSpendValue,
					vendorSpendUnit
				),
				customCurrencies: customCurrency.code ? [customCurrency] : [],
				...this.getPeriodsData(),
			}),
		]).then(async () => {
			await refetchSpendCubeSettings();
			await refetchSettingsOptions();
			await getPorfolioCompanies().then(porfolioResponse => {
				let companiesOptions;
				if (
					porfolioResponse.data !== null &&
					porfolioResponse.data.length > 0
				) {
					companiesOptions = porfolioResponse.data.map(x => {
						return {
							value: x,
							label: x,
						};
					});
				}
				this.setState({
					portfolioCompanies: {
						options: companiesOptions,
						isLoading: false,
					},
				});
			});
			if (isFirstTimeUsage) {
				fetchState(true);
			} else {
				if (typeof refetchSettings === 'function') {
					refetchSettings();

					this.setState({
						spendCubeSettings: {
							...this.state.spendCubeSettings,
							shouldRefresh: true,
						},
					});
				}
			}
		});
	};

	getValueByUnitPair = (value, unit) => {
		return value * UNITS.find(item => item.key === unit).worth;
	};

	handleReturnTailSpend = async () => {
		const historyState = this.props.location.state;

		if (historyState && historyState.backRoute) {
			this.props.history.push(historyState.backRoute);
		}
	};

	handleMetadataSave = async () => {
		const { saveExportMetadata, history } = this.props;
		const {
			exportedMetadata,
			isEmptyMetaData,
			anonymizationLevel,
			serverMetaData,
		} = this.state;

		// In case when we just edit anonymizationLevel we need to take saved metadata which comes from server
		const metadata = exportedMetadata || serverMetaData;
		if (isEmptyMetaData && !anonymizationLevel) {
			return this.setState({
				isStale: false,
				addAnonymizationLevelStep: true,
				isEmptyMetaData: false,
			});
		}
		metadata.anonymizationLevel = anonymizationLevel
			? anonymizationLevel
			: serverMetaData.anonymizationLevel;

		await saveExportMetadata(metadata);

		this.setState(
			{
				exportedMetadata: null,
				isStale: false,
				isFormValid: false,
				anonymizationLevel: null,
				isEmptyMetaData: true,
				serverMetaData: null,
				addAnonymizationLevelStep: false,
			},
			() => {
				history.push(`${EXPORT_PAGE_PATH}/saved`);
			}
		);
	};

	saveCustomFilters = () => {
		const {
			updateSettingsConfig,
			selectedcustomFiltersValues,
			selectedcustomFilters,
			setQueryStringStateAndPush,
			customFilters: customFiltersProps,
		} = this.props;
		const { customFilters } = this.state;

		if (customFilters === customFiltersProps) {
			return Promise.resolve();
		}

		return updateSettingsConfig({
			customFilters,
		}).then(() => {
			this.setState({
				isStale: false,
			});

			// if filters are not valid - clear the filters from query string
			if (selectedcustomFiltersValues.length !== selectedcustomFilters.length) {
				setQueryStringStateAndPush({
					customFiltersValues: [],
					customFilters: [],
				});
				return;
			}

			const activeCustomFiltersNames = customFilters
				? customFilters
						.filter(({ isActive }) => isActive)
						.map(({ name }) => name)
				: [];

			const activeSelectedFilters = selectedcustomFilters
				.map((name, index) => ({
					name,
					value: selectedcustomFiltersValues[index],
				}))
				.filter(({ name }) => activeCustomFiltersNames.includes(name));

			setQueryStringStateAndPush({
				customFilters: activeSelectedFilters.map(({ name }) => name),
				customFiltersValues: activeSelectedFilters.map(({ value }) => value),
			});
		});
	};

	onMetadataLoad = metadata => {
		const isEmptyMetaData = !Object.values(metadata).filter(x => !!x).length;
		this.setState({ isEmptyMetaData, serverMetaData: metadata });
	};

	clearStaleState = () => {
		this.setState({ isStale: false });
	};
}

export const Settings = compose(
	withMutation('/companyMetadata', {
		method: 'POST',
		name: 'saveExportMetadata',
	})
)(SettingsBase);

export default props => (
	<PermissionsContext.Consumer>
		{({ permissions }) => (
			<ApiDataContext.Consumer>
				{({ clearApiCache }) => (
					<SpendCubeSettingsContext.Consumer>
						{({ settings, refetch: refetchSpendCubeSettings }) => (
							<SettingsContext.Consumer>
								{({
									companiesMetadata,
									opportunityYear,
									customFilters,
									hideDecimals,
									aliasName,
									companyName,
									appTitle,
									backgroundColor,
									foregroundColor,
									threshold1,
									threshold2,
									updateSettingsConfig,
									tailSpendAnalysisData,
								}) => (
									<QueryStringContext.Consumer>
										{({
											customFilters: selectedcustomFilters,
											customFiltersValues: selectedcustomFiltersValues,
											setQueryStringStateAndPush,
										}) => (
											<UploadStateContext.Consumer>
												{({ status, refetch }) => (
													<ClassificationContext.Consumer>
														{({ refetch: refetchClassification }) => (
															<SettingsOptionsContext.Consumer>
																{({ refetch: refetchSettingsOptions }) => (
																	<Settings
																		{...props}
																		companiesMetadata={companiesMetadata}
																		refetchClassification={
																			refetchClassification
																		}
																		refetchSpendCubeSettings={
																			refetchSpendCubeSettings
																		}
																		refetchSettingsOptions={
																			refetchSettingsOptions
																		}
																		clearApiCache={clearApiCache}
																		opportunityYear={opportunityYear}
																		customFilters={customFilters}
																		hideDecimals={hideDecimals}
																		aliasName={aliasName}
																		companyName={companyName}
																		appTitle={appTitle}
																		backgroundColor={backgroundColor}
																		foregroundColor={foregroundColor}
																		threshold1={threshold1}
																		threshold2={threshold2}
																		updateSettingsConfig={updateSettingsConfig}
																		status={status}
																		fetchState={refetch}
																		setQueryStringStateAndPush={
																			setQueryStringStateAndPush
																		}
																		tailSpendAnalysisData={
																			tailSpendAnalysisData
																		}
																		selectedcustomFiltersValues={
																			selectedcustomFiltersValues
																				? Array.isArray(
																						selectedcustomFiltersValues
																				  )
																					? selectedcustomFiltersValues
																					: [selectedcustomFiltersValues]
																				: []
																		}
																		selectedcustomFilters={
																			selectedcustomFilters
																				? Array.isArray(selectedcustomFilters)
																					? selectedcustomFilters
																					: [selectedcustomFilters]
																				: []
																		}
																		permissions={permissions || []}
																		taxonomyVersion={settings.taxonomyVersion}
																		isParent={settings.isParent || false}
																		isPortco={settings.isPortco || false}
																	/>
																)}
															</SettingsOptionsContext.Consumer>
														)}
													</ClassificationContext.Consumer>
												)}
											</UploadStateContext.Consumer>
										)}
									</QueryStringContext.Consumer>
								)}
							</SettingsContext.Consumer>
						)}
					</SpendCubeSettingsContext.Consumer>
				)}
			</ApiDataContext.Consumer>
		)}
	</PermissionsContext.Consumer>
);
